import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import ImageWithFallback from '@/components/ImageWithFallback';
import { useLanguage } from '@/App';
import { API } from '@/App';
import { Calendar, User, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';
import { tr, enUS } from 'date-fns/locale';

const BlogDetailPage = () => {
  const { slug } = useParams();
  const { language } = useLanguage();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  const content = {
    tr: {
      loading: 'Yükleniy or...',
      notFound: 'Blog yazısı bulunamadı',
      back: 'Blog\'a Dön',
    },
    en: {
      loading: 'Loading...',
      notFound: 'Blog post not found',
      back: 'Back to Blog',
    },
  };

  const t = content[language];

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await axios.get(`${API}/blog/${slug}`);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex items-center justify-center min-h-[60vh] pt-20">
          <div className="text-gray-600">{t.loading}</div>
        </div>
        <Footer />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen">
        <Navbar />
        <div className="flex flex-col items-center justify-center min-h-[60vh] pt-20">
          <div className="text-gray-600 mb-4">{t.notFound}</div>
          <Link to="/blog">
            <Button variant="outline">{t.back}</Button>
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Navbar />

      {/* Header */}
      <div className="pt-24 pb-12 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto">
          <Link to="/blog">
            <Button variant="ghost" size="sm" className="mb-6" data-testid="back-to-blog">
              <ArrowLeft className="mr-2 w-4 h-4" />
              {t.back}
            </Button>
          </Link>
          <h1 
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            style={{ fontFamily: 'Space Grotesk, sans-serif' }}
            data-testid="blog-detail-title"
          >
            {language === 'tr' ? post.title_tr : post.title_en}
          </h1>
          <div className="flex items-center gap-6 text-gray-600">
            <div className="flex items-center gap-2">
              <User className="w-5 h-5" />
              <span>{post.author}</span>
            </div>
            <div className="flex items-center gap-2">
              <Calendar className="w-5 h-5" />
              <span>
                {format(new Date(post.created_at), 'dd MMMM yyyy', {
                  locale: language === 'tr' ? tr : enUS
                })}
              </span>
            </div>
          </div>
        </div>
      </div>

      {/* Featured Image */}
      <div className="px-4 -mt-8">
        <div className="max-w-4xl mx-auto">
          <ImageWithFallback
            src={post.image_url} 
            alt={language === 'tr' ? post.title_tr : post.title_en}
            fallbackType="blog"
            className="w-full h-96 object-cover rounded-xl shadow-lg"
            data-testid="blog-detail-image"
          />
        </div>
      </div>

      {/* Content */}
      <article className="py-12 px-4">
        <div className="max-w-4xl mx-auto">
          <div 
            className="prose prose-lg max-w-none"
            style={{
              color: '#374151',
              lineHeight: '1.8',
            }}
            data-testid="blog-detail-content"
          >
            {(language === 'tr' ? post.content_tr : post.content_en).split('\n').map((paragraph, index) => (
              <p key={index} className="mb-4">{paragraph}</p>
            ))}
          </div>
        </div>
      </article>

      <Footer />
    </div>
  );
};

export default BlogDetailPage;